const _memoize = require('lodash/memoize');
const {parse, format} = require('url');
const urlJoin = require('url-join');
const locales = require('./locales');

/**
 * @typedef {import('./locales/locales.js').LocaleData} LocaleData
 */

/**
 * Get TLD by hostname
 * @param {string} hostname Hostname
 * @returns {string} TLD
 */
function getTld(hostname = '') {
    return hostname.split('.').pop() || 'ru';
}

/**
 * Set new TLD for current hostname
 * @param {string} hostname Hostname
 * @param {string} tld New TLD
 * @returns {string} New hostname
 */
function setTld(hostname, tld) {
    return hostname.replace(/\.(ru|com)$/, `.${tld}`);
}

/**
 * Get locale by hostname and pathname
 * @param {string} hostname Hostname
 * @param {string} pathname Pathname
 * @returns {LocaleData} Locale data
 */
function getLocale(hostname = '', pathname = '/') {
    const code = getLocaleCode(hostname, pathname);

    if (!locales.isRegistered(code)) {
        throw new Error(`Unregistered locale code: '${code}'`);
    }

    return locales.getLocale(code);
}

/**
 * Get locale by lang
 * @param {string} lang Lang
 * @param {string} tld TLD
 * @returns {LocaleData} Locale data
 */
function getLocaleByLang(lang = '', tld = lang) {
    const registeredLocales = locales.getLocales();

    const langLocales = registeredLocales.filter((locale) => locale.lang === lang);
    const tldLocales = langLocales.filter((locale) => locale.tld === tld);

    const resultLocales = tldLocales.length > 0 ? tldLocales : langLocales;

    return resultLocales.length > 0
        ? resultLocales.find((locale) => locale.region === lang) ||
              resultLocales.find((locale) => locale.region === 'int') ||
              resultLocales[0]
        : registeredLocales.find((locale) => locale.region === 'int');
}

/**
 * Get locale by tld and region
 * @param {string} region Region
 * @param {string} lang Lang
 * @param {string} tld TLD
 * @returns {LocaleData} Locale data
 */
function getLocaleByRegion(region = '', lang = region, tld = lang) {
    const geoRegion = region && region.toLowerCase();
    const registeredLocales = locales.getLocales();

    const regionLocales = registeredLocales.filter((locale) => locale.region === geoRegion);

    return regionLocales.length > 0
        ? regionLocales.find((locale) => locale.lang === lang) ||
              regionLocales.find((locale) => locale.tld === tld) ||
              regionLocales[0]
        : registeredLocales.find((locale) => locale.region === 'int');
}

/**
 * Get locale code by hostname and pathname
 * @param {string} hostname Hostname
 * @param {string} pathname Pathname
 * @returns {string} Locale code
 */
function getLocaleCode(hostname = '', pathname = '/') {
    return getLocaleCodeFromPathname(pathname) || locales.getLocaleCodeByTld(getTld(hostname));
}

/**
 * Get locale code from pathname
 * @param {string} pathname Pathname
 * @returns {string | undefined} Locale code
 */
function getLocaleCodeFromPathname(pathname = '/') {
    const normalizedPathname =
        pathname && pathname.startsWith('/') ? pathname.substring(1) : pathname;
    const code = normalizedPathname.split('/').shift();
    return locales.isRegistered(code) ? code : undefined;
}

/**
 * Check if url has locale prefix
 * @param {string} url URL
 * @param {string} code Locale code to compare with (if specified)
 * @returns {boolean} Boolean
 */
function hasLocalePrefix(url = '/', code = '') {
    const {pathname} = parse(url);
    const localeCode = getLocaleCodeFromPathname(pathname);
    return Boolean(localeCode && (!code || localeCode.toLowerCase() === code.toLowerCase()));
}

/**
 * Add locale prefix to pathname
 * @param {string} url URL
 * @param {string} code Locale code
 * @returns {string} Url without locale prefix
 */
function addLocalePrefix(url = '/', code = '') {
    const {hash, pathname, search} = parse(url);
    if (!url || !code || hasLocalePrefix(pathname)) {
        return url;
    }

    return format({
        pathname: urlJoin('/', code.toLowerCase(), pathname),
        search,
        hash,
    });
}

/**
 * Remove locale prefix from url
 * @param {string} url URL
 * @param {string} code Locale code
 * @returns {string} Url without locale prefix
 */
function removeLocalePrefix(url = '/') {
    const {hash, pathname, search} = parse(url);
    const code = getLocaleCodeFromPathname(pathname);

    return format({
        pathname: code ? pathname.substring(code.length + 1) || '/' : pathname,
        search,
        hash,
    });
}

/**
 * Replace locale prefix in url
 * @param {string} url URL
 * @param {string} code Locale code
 * @returns {string} Url without locale prefix
 */
function replaceLocalePrefix(url = '/', code = '') {
    if (!url || !code) {
        return url;
    }

    let newUrl = url;

    if (hasLocalePrefix(url)) {
        newUrl = removeLocalePrefix(url);
    }

    return addLocalePrefix(newUrl, code);
}

/**
 * Get relative locale URL by current hostname, url and locale code
 * @param {LocaleData} locale Locale
 * @param {string} url URL
 * @returns {string} Locale URL
 */
function getLocaleUrl(locale, url = '/') {
    const {hash, pathname, search} = parse(url);

    const localePathname =
        locale.tld === locale.lang
            ? removeLocalePrefix(pathname)
            : replaceLocalePrefix(pathname, locale.code);

    return format({
        pathname: localePathname,
        search,
        hash,
    });
}

/**
 * Get absolute locale URL by current hostname, url and locale code
 * @param {LocaleData} locale Locale
 * @param {string} hostname Hostname
 * @param {string} url URL
 * @returns {string} Locale URL
 */
function getAbsoluteLocaleUrl(locale, hostname, url = '/') {
    try {
        const {hash, pathname, port, protocol, search} = new URL(url, `https://${hostname}`);

        const localeHostname = setTld(hostname, locale.tld);
        const localePathname =
            locale.tld === locale.lang
                ? removeLocalePrefix(pathname)
                : replaceLocalePrefix(pathname, locale.code);

        return format({
            protocol,
            hostname: localeHostname,
            port,
            pathname: localePathname,
            search,
            hash,
        });
    } catch (ex) {
        return url;
    }
}

/**
 * Get locale formatter by locale code
 * @param {string} code Locale code
 * @returns {object} Intl number formatter
 */
const getCurrencyFormatter = _memoize(
    (code, isInteger) => {
        const {currency} = locales.getLocale(code);

        return new Intl.NumberFormat(code, {
            currency,
            style: 'currency',
            currencyDisplay: 'symbol',
            minimumFractionDigits: isInteger ? 0 : undefined,
            maximumFractionDigits: isInteger ? 0 : 4,
        });
    },
    (...args) => JSON.stringify(args),
);

module.exports = {
    getTld,
    setTld,
    getLocale,
    getLocaleByLang,
    getLocaleByRegion,
    hasLocalePrefix,
    addLocalePrefix,
    removeLocalePrefix,
    replaceLocalePrefix,
    getLocaleUrl,
    getAbsoluteLocaleUrl,
    getCurrencyFormatter,
    getLocaleCode,
};
